// §34.10 (D-494) — Snip's crop arithmetic, as one pure function.
//
// It lives here, alone, for the reason M63.1 names it: **this is the part a screenshot review cannot
// check.** A crop that is off by a device-pixel ratio produces a picture that looks entirely
// plausible — a window's worth of desktop, correctly framed, just the wrong window's worth — and the
// only way to be sure is a truth table.
//
// It is in `shared` because both halves need it and §6.5 lets them share exactly one package: the
// capability in `host/capabilities.ts` converts a window rect, and the app converts the rect the
// picker overlay dragged. Two copies of this would be two chances to get it wrong.
//
// ── The three coordinate spaces, and why there are three
//
//   1. **CSS pixels.** What the window manager stores (§5.1) and what a pointer event reports. The
//      desktop is `innerWidth` × `innerHeight` of them.
//   2. **Device pixels.** CSS pixels × `devicePixelRatio`. What the screen actually has.
//   3. **Capture pixels.** What `ImageCapture.grabFrame()` handed back — and this is the one that
//      cannot be assumed. `getDisplayMedia` honours the track's constraints and the browser's own
//      limits, so the bitmap may be the device resolution, or the CSS resolution (DPR 1 machines),
//      or **downscaled** on a machine whose display is larger than the encoder's cap. A crop written
//      against `devicePixelRatio` alone is correct on the developer's laptop and wrong on a 5K
//      monitor, which is exactly the bug this file exists to make impossible.
//
// So the scale that matters is `capturedWidth / innerWidth` — measured from the frame in hand rather
// than inferred from the display — and `devicePixelRatio` never appears in the arithmetic at all. It
// is a *consequence* of the capture, not an input to it: on a DPR-2 machine whose capture came back
// at device resolution the measured ratio simply *is* 2.

/** A rectangle in CSS pixels — the space the WM and pointer events both speak. */
export interface CssRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** A rectangle in capture-bitmap pixels — what `drawImage`'s source arguments want. */
export interface CaptureRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** The frame in hand, and the viewport it was taken of. */
export interface CaptureFrame {
  readonly capturedWidth: number;
  readonly capturedHeight: number;
  readonly innerWidth: number;
  readonly innerHeight: number;
}

/**
 * Map a CSS-pixel rect onto the captured bitmap, clamped to it.
 *
 * **X and Y are scaled independently.** A capture is not guaranteed to preserve aspect ratio — a
 * browser that caps one dimension can hand back a frame that is letterboxed in the other — and one
 * shared scale factor would shear every crop taken on such a machine. Two ratios cost nothing and
 * are correct in both cases, since on a proportional capture they are the same number.
 *
 * **Clamped, not validated.** A drag that ended outside the viewport, or a window the user moved
 * half off-screen (§5.2 allows it), produces a rect that is partly outside the frame; `drawImage`
 * answers a transparent band for the outside part rather than an error, which would be a picture with
 * a mystery in it. Clamping means the snip is the part that existed.
 *
 * Rounded **outward** — floor the origin, ceil the far edge — so a region a user dragged around
 * something never crops a pixel *off* it. A half-pixel of neighbouring desktop is invisible; a
 * half-pixel of the subject missing is the kind of thing that gets noticed and cannot be explained.
 */
export function captureRect(rect: CssRect, frame: CaptureFrame): CaptureRect {
  const scaleX = frame.innerWidth > 0 ? frame.capturedWidth / frame.innerWidth : 1;
  const scaleY = frame.innerHeight > 0 ? frame.capturedHeight / frame.innerHeight : 1;

  const left = Math.max(0, Math.min(frame.capturedWidth, Math.floor(rect.x * scaleX)));
  const top = Math.max(0, Math.min(frame.capturedHeight, Math.floor(rect.y * scaleY)));
  const right = Math.max(left, Math.min(frame.capturedWidth, Math.ceil((rect.x + rect.width) * scaleX)));
  const bottom = Math.max(top, Math.min(frame.capturedHeight, Math.ceil((rect.y + rect.height) * scaleY)));

  return { x: left, y: top, width: right - left, height: bottom - top };
}

/** The whole frame, for the *full desktop* mode — which needs no crop and says so. */
export const wholeFrame = (frame: CaptureFrame): CaptureRect => ({
  x: 0,
  y: 0,
  width: frame.capturedWidth,
  height: frame.capturedHeight,
});

/**
 * Normalise a drag into a rect, whichever corner it started from.
 *
 * A user drags up-and-left as readily as down-and-right, and a negative width is not a rectangle any
 * of the above would survive.
 */
export function dragRect(
  from: { x: number; y: number },
  to: { x: number; y: number },
): CssRect {
  return {
    x: Math.min(from.x, to.x),
    y: Math.min(from.y, to.y),
    width: Math.abs(to.x - from.x),
    height: Math.abs(to.y - from.y),
  };
}

/** A drag too small to be a deliberate region — treated as a cancel rather than a 2-pixel snip. */
export const MIN_REGION_PX = 8;

export const isUsableRegion = (rect: CssRect): boolean =>
  rect.width >= MIN_REGION_PX && rect.height >= MIN_REGION_PX;
